import { useState, useEffect } from 'react';
import { findAccountIdByEmail, getAccountById } from '../services/firestoreService';
import { getInternalUsers } from '../services/userService';

/**
 * Resolves the client's Success Manager as a questions assignee (internal_users id + display name).
 * @returns {{ assignee: { id: string, name: string } | null, loading: boolean }}
 */
export function useSuccessManagerQuestionAssignee(currentUser, companyId) {
  const [assignee, setAssignee] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!currentUser?.email && !companyId) {
        setAssignee(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const accountId =
          companyId || (await findAccountIdByEmail(currentUser.email, 'Clients'));
        if (!accountId || cancelled) {
          setAssignee(null);
          return;
        }

        const account = await getAccountById(accountId);
        if (cancelled) return;

        if (!account?.successManagerId) {
          setAssignee(null);
          return;
        }

        let name = account.successManager?.trim() || null;
        if (!name) {
          try {
            const users = await getInternalUsers();
            const sm = users.find((u) => u.id === account.successManagerId);
            name = sm?.name || 'Success Manager';
          } catch (_) {
            name = 'Success Manager';
          }
        }

        setAssignee({ id: account.successManagerId, name });
      } catch (e) {
        console.error('useSuccessManagerQuestionAssignee:', e);
        if (!cancelled) setAssignee(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [currentUser?.email, companyId]);

  return { assignee, loading };
}
